<template>
	<div class="skischool-card">
		<div class="grid-x grid-margin-x">
			<!-- Image -->
			<div class="cell small-12 medium-4 large-3">
				<img
					class="skischool-card__image"
					:src="getImgUrl(cardData.imageurl)"
					:alt="cardData.name"
				/>
			</div>

			<!-- Description -->
			<div class="cell small-12 medium-6">
				<h2>
					<strong>
						{{ cardData.name }} ({{
							formatAgeSpan(cardData.minage, cardData.maxage)
						}})
					</strong>
					{{ dateDiff(cardData.startdate, cardData.enddate) }}
					<template v-if="dateDiff(cardData.startdate, cardData.enddate) == 1">
						{{ $t('day') }} ({{ cardData.startday }})
					</template>
					<template v-else>
						{{ $t('days') }} ({{ formatWeekday(cardData.startday) }}-{{
							formatWeekday(cardData.endday)
						}})
					</template>
				</h2>
				<div
					class="skischool-card__levels"
					v-if="cardData.activitylevel > 0 || cardData.activitytype > 0"
				>
					<template v-if="cardData.activitytype > 0">
						<CategoryLabels
							:value="types.find(x => x.id === cardData.activitytype).label"
						/>
					</template>
					<template
						v-if="
							cardData.activitylevel > 0 && cardData.activitylevel.length != 5
						"
					>
						<LevelLabel
							v-for="level in cardData.activitylevel"
							:key="level"
							:level="level"
							:tooltip="$t('skischool.level' + level + 'Tooltip')"
						>
							{{ getLevelName(cardData.activitylevel) }}
						</LevelLabel>
					</template>
				</div>
				<p
					:class="[
						'skischool-card__description',
						{ 'skischool-card__description--open': showFullDescription }
					]"
					ref="cardDescription"
				>
					<span v-html="convertLineBreak(cardData.description)"></span>
				</p>
				<Button
					v-if="showDescriptionButton"
					class="skischool-card__button-description"
					size="xsmall"
					color="primary4"
					@click.native="toggleDescription"
				>
					<span v-if="!showFullDescription">
						{{ $t('skischool.showMore') }}
					</span>
					<span v-if="showFullDescription">{{ $t('skischool.showLess') }}</span>
				</Button>
			</div>

			<!-- Toogle button -->
			<div class="cell small-12 medium-3">
				<div class="skischool-card__price">
					{{ convertPrice(cardData.price) }} SEK
				</div>
				<Button
					v-if="!showOccasions"
					class="skischool-card__button-occasions"
					:full="true"
					@click.native="toggleOccasions"
				>
					{{ $t('skischool.showAlternatives') }}
				</Button>
				<Button
					v-if="showOccasions"
					class="skischool-card__button-occasions"
					color="primary4"
					:full="true"
					@click.native="toggleOccasions"
				>
					{{ $t('skischool.hideAlternatives') }}
				</Button>
			</div>
		</div>

		<!-- Occasions -->
		<transition
			name="collapse"
			v-on:before-enter="beforeEnter"
			v-on:enter="enter"
			v-on:before-leave="beforeLeave"
			v-on:leave="leave"
		>
			<div
				class="skischool-card__occasions"
				v-show="showOccasions"
				ref="element"
			>
				<!-- occasions mobile select -->
				<div class="skischool-card__occasions-mobile">
					<div class="grid-x grid-margin-x">
						<div class="cell small-12">
							<h3>{{ $t('skischool.chooseOccasion') }}:</h3>
							<Select
								@input="onChangeOccasion"
								:lightTheme="true"
								:option-items="
									createOccasionsArray(
										cardData.occasions,
										cardData.startdate,
										cardData.enddate
									)
								"
							></Select>
						</div>

						<div class="cell small-12" v-if="occasionsId != 0">
							<div class="grid-x grid-margin-x">
								<div class="cell small-12">
									<h3>
										{{ $t('skischool.placeholderStartDate') }}
										{{ $t('skischool.group') }}:
									</h3>
								</div>
								<div
									class="cell small-6 skischool-card__occasions-group-mobile"
									v-for="i in amountOfGroups"
									:key="i"
								>
									{{ $t('skischool.group') }} {{ i }}
									<div
										v-for="occasion in cardData.occasions"
										:key="occasion.id"
									>
										<template
											v-if="
												occasion.groups[i - 1] && occasion.id == occasionsId
											"
										>
											<SelectArray
												:data="{
													id: occasion.groups[i - 1].activitylid,
													type: 'skischool',
													index: index,
													poollid: cardData.poollid,
													ageGroup: formatAgeSpan(
														cardData.minage,
														cardData.maxage
													),
													fromAge: cardData.minage,
													toAge: cardData.maxage,
													levels: createLevelArray(cardData.activitylevel),
													price: cardData.price,
													displayLength:
														dateDiff(cardData.startdate, cardData.enddate) +
														' ' +
														dayordays(
															dateDiff(cardData.startdate, cardData.enddate)
														),
													startDate: cardData.startdate,
													endDate: cardData.enddate,
													name: cardData.name,
													image: getImgUrl(cardData.imageurl),
													groupName: 'Grupp ' + i,
													startTime: occasion.fromtime,
													endTime: occasion.totime,
													pricecode: cardData.pricecode,
													activitylid: occasion.groups[i - 1].activitylid
												}"
												:placeholder="$t('skischool.placeholderQuantity')"
												:option-items="
													createSeatsArray(
														occasion.groups[i - 1].availableseats
													)
												"
												:lightTheme="true"
												:small="true"
											/>
											<div
												v-if="occasion.groups[i - 1].availableseats < 3"
												class="skischool-card__occasions-splash skischool-card__occasions-splash--mobile"
											>
												{{ $t('skischool.fewSpotsLeft') }}
											</div>
										</template>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="skischool-card__occasions-inner">
					<!-- Table head -->
					<div class="grid-x grid-margin-x">
						<div class="cell small-9 small-offset-3">
							<h3>{{ $t('skischool.chooseOccasion') }}:</h3>
						</div>
						<div class="cell small-3 skischool-card__occasions-occasion"></div>

						<div
							v-for="occasion in cardData.occasions"
							:key="occasion.id"
							class="cell small-2 skischool-card__occasions-occasion"
						>
							<span class="skischool-card__occasions-date">
								{{ formatDateSpan(cardData.startdate, cardData.enddate) }}
							</span>
							<span class="skischool-card__occasions-time">
								{{ occasion.fromtime }}-{{ occasion.totime }}
							</span>
						</div>
					</div>

					<!-- Table rows -->
					<div
						class="grid-x grid-margin-x"
						v-for="i in amountOfGroups"
						:key="i"
					>
						<div class="cell small-3 skischool-card__occasions-groupname">
							{{ $t('skischool.group') }} {{ i }}
						</div>
						<div
							class="cell small-2 skischool-card__occasions-group"
							v-for="occasion in cardData.occasions"
							:key="occasion.id"
						>
							<template v-if="occasion.groups[i - 1]">
								<SelectArray
									:data="{
										id: occasion.groups[i - 1].activitylid,
										type: 'skischool',
										index: index,
										poollid: cardData.poollid,
										ageGroup: formatAgeSpan(cardData.minage, cardData.maxage),
										fromAge: cardData.minage,
										toAge: cardData.maxage,
										levels: createLevelArray(cardData.activitylevel),
										price: cardData.price,
										displayLength:
											dateDiff(cardData.startdate, cardData.enddate) +
											' ' +
											dayordays(dateDiff(cardData.startdate, cardData.enddate)),
										startDate: cardData.startdate,
										endDate: cardData.enddate,
										name: cardData.name,
										image: getImgUrl(cardData.imageurl),
										groupName: 'Grupp ' + i,
										startTime: occasion.fromtime,
										endTime: occasion.totime,
										pricecode: cardData.pricecode,
										activitylid: occasion.groups[i - 1].activitylid
									}"
									:placeholder="$t('skischool.placeholderQuantity')"
									:option-items="
										createSeatsArray(occasion.groups[i - 1].availableseats)
									"
									:lightTheme="true"
									:small="true"
								/>
								<div
									v-if="occasion.groups[i - 1].availableseats < 3"
									class="skischool-card__occasions-splash"
								>
									{{ $t('skischool.fewSpotsLeft') }}
								</div>
							</template>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import mixin from '../../mixin'
import store from '../../vueX/store'
import Select from '../atoms/Select'
import SelectArray from '../atoms/SelectArray'
import CategoryLabels from '../atoms/CategoryLabels'
import LevelLabel from '../atoms/LevelLabel'
import Button from '../atoms/Button.vue'

export default {
	name: 'SkischoolCard',
	mixins: [mixin],
	components: {
		SelectArray,
		Select,
		CategoryLabels,
		LevelLabel,
		Button
	},
	props: {
		cardData: {
			type: Object,
			default: () => {}
		},
		levels: {
			type: Array,
			default: () => []
		},
		types: {
			type: Array,
			default: () => []
		},
		index: {
			type: Number
		}
	},
	data() {
		return {
			showOccasions: false,
			showFullDescription: false,
			showDescriptionButton: true,
			skischoolQuantity: '',
			selectedQuantity: 'Välj',
			amountOfGroups: 0,
			occasionsId: 0
		}
	},
	methods: {
		onChangeOccasion(val) {
			this.$refs.element.style.height = 'auto'
			this.occasionsId = val
		},
		// selects the correct occasions on mobile
		createOccasionsArray(occasion, startdate, enddate) {
			var array = []
			var object = {}
			const setdate = this.formatDateSpan(startdate, enddate)

			for (var i = 0; i < occasion.length; i++) {
				object = {
					id: occasion[i].id,
					text:
						occasion[i].fromtime +
						' - ' +
						occasion[i].totime +
						' ' +
						'(' +
						setdate +
						')'
				}
				array.push(object)
			}
			return array
		},

		createSeatsArray(seats) {
			var array = [{ id: 0, text: '0 st' }]
			var object = {}

			for (var i = 0; i < seats; i++) {
				object = { id: i + 1, text: i + 1 + ' st' }
				array.push(object)
			}
			return array
		},
		toggleOccasions() {
			this.showOccasions = !this.showOccasions
			if (this.showOccasions) {
				store.dispatch('GTM_PUSH_SKISCHOOL_DETAILS', {
					item: this.cardData,
					levels: this.levels,
					index: this.index
				})
			}
		},
		toggleDescription() {
			this.showFullDescription = !this.showFullDescription
			if (this.showOccasions) {
				store.dispatch('GTM_PUSH_SKISCHOOL_DETAILS', {
					item: this.cardData,
					levels: this.levels,
					index: this.index
				})
			}
		},
		createLevelArray(ids) {
			var array = []
			var object = {}
			var levelName = ''
			var levels = this.levels

			ids.forEach(function (levelId) {
				levelName = levels.find(each => each.id == levelId).label
				object = { id: levelId, label: levelName }
				array.push(object)
			})
			return array
		},
		beforeEnter: function (el) {
			el.style.height = '0'
		},
		enter: function (el) {
			el.style.height = el.scrollHeight + 'px'
		},
		beforeLeave: function (el) {
			el.style.height = el.scrollHeight + 'px'
		},
		leave: function (el) {
			el.style.height = '0'
		},
		hideDescriptionButton() {
			let height = this.$refs.cardDescription.clientHeight
			if (height < 49) {
				this.showDescriptionButton = false
			}
		}
	},
	mounted: function () {
		let longest = -1
		this.cardData.occasions.forEach(function (occasion) {
			if (occasion.groups.length > longest) {
				longest = occasion.groups.length
			}
		})
		this.amountOfGroups = longest

		this.hideDescriptionButton()
	}
}
</script>

<style scoped lang="scss">
.skischool-card {
	padding: 40px 0;
	position: relative;
	&:after {
		content: '';
		display: block;
		position: absolute;
		bottom: 0;
		width: calc(100% - 4rem);
		border-bottom: 1px solid #d4d8df;
		margin-left: 2rem;
		margin-right: 2rem;
	}

	&:last-child:after {
		display: none;
	}

	&__image {
		width: 100%;
	}

	h2 {
		font-size: 28px;
		strong {
			font-weight: 500;
		}
	}

	&__button-occasions {
		float: right;
	}

	&__description {
		max-height: 49px;
		overflow: hidden;
		margin-bottom: 0;
	}
	&__description--open {
		max-height: 999px;
	}

	&__button-description {
		margin-top: 10px;
	}

	&__levels {
		margin: 10px 0;
		.category-label,
		.level {
			margin: 0 10px 10px 0;
			vertical-align: middle;
		}
	}

	&__price {
		color: #434449;
		font-size: 22px;
		font-weight: 500;
		line-height: 28px;
		text-align: center;
		margin: 35px 0 20px;
	}

	&__occasions {
		overflow: hidden;
		&-inner {
			display: none;
			@include for-tablet-portrait-up {
				display: block;
			}
			margin: 10px 0 10px;
			.grid-x {
				padding: 15px 0;
				&:nth-child(even) {
					background: #f4f5f7;
				}
			}
		}
		&-mobile {
			display: block;
			margin: 10px 0 10px;
			@include for-tablet-portrait-up {
				display: none;
			}
		}
		h3 {
			font-size: 22px;
			font-weight: 500;
			color: #434449;
			margin: 16px 0;
			@include for-tablet-portrait-up {
				margin: 0 0 16px 0;
			}
		}
		&-occasion,
		&-group,
		&-groupname {
			position: relative;
			&:after {
				content: '';
				display: block;
				position: absolute;
				right: -2rem;
				top: -15px;
				height: calc(100% + 30px);
				border-right: 1px solid #d4d8df;
			}
			&:last-child {
				&:after {
					display: none;
				}
			}
		}
		&-occasion {
			&:after {
				top: 0;
				height: calc(100% + 15px);
			}
		}

		&-date {
			display: block;
			color: #434449;
			font-size: 14px;
			line-height: 22px;
			margin-bottom: 5px;
			strong {
				font-weight: 500;
			}
		}
		&-time {
			display: block;
			color: #434449;
			font-family: Saira;
			font-size: 22px;
			font-weight: 500;
			letter-spacing: 0;
			line-height: 28px;
		}
		&-groupname {
			text-align: right;
			line-height: 35px;
		}
		&-group-mobile {
			position: relative;
		}
		&-splash {
			display: inline-block;
			color: #ffffff;
			font-size: 9px;
			font-weight: 600;
			line-height: 14px;
			text-transform: uppercase;
			border-radius: 3px;
			background-color: #7fc3bb;
			padding: 2px 5px;

			position: absolute;
			bottom: -10px;
			right: -10px;
			&--mobile {
				right: 5px;
			}
		}
	}
}
</style>
